import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AnimatePresence } from 'framer-motion'
import { useLocation } from 'react-router'

import { BusinessRoute } from '@/constants/paths'
import { queryKeys } from '@/constants/queryKeys'
import { Loader, ProgressButton } from '@/shared/components'
import { FullScreen } from '@/shared/ui'

import { getBankEligibility } from '../../api'

import { ProofOfAddressDocumentsScreen } from './components/ProofOfAddressDocumentsScreen'
import { ProofOfAddressScreen } from './components/ProofOfAddressScreen'

enum Step {
  ProofOfAddress = 'proofOfAddress',
  ProofOfAddressDocuments = 'proofOfAddressDocuments',
}

type State =
  | { type: Step.ProofOfAddress }
  | { type: Step.ProofOfAddressDocuments }

export const KYB = () => {
  const location = useLocation()

  const [activeStep, setActiveStep] = useState<State>({
    type: Step.ProofOfAddress,
  })

  const [ownerId, setOwnerId] = useState<string>()

  const bankEligibilityQuery = useQuery({
    queryKey: [queryKeys.getBankEligibility],
    queryFn: () => getBankEligibility(),
    select: (data) => data.data,
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [activeStep])

  const progressByStep = useMemo(() => {
    switch (activeStep.type) {
      case Step.ProofOfAddress:
        return 50
      case Step.ProofOfAddressDocuments:
        return 50

      default:
        return 0
    }
  }, [activeStep.type])

  if (bankEligibilityQuery.isPending) {
    return <Loader />
  }

  return (
    <FullScreen>
      <ProgressButton
        progress={progressByStep}
        to={location.state?.from ?? BusinessRoute.Dashboard}
      />

      <AnimatePresence mode="wait">
        {activeStep.type === Step.ProofOfAddress && (
          <ProofOfAddressScreen
            bankEligibility={bankEligibilityQuery.data}
            onOwnerClick={(ownerId) => {
              setOwnerId(ownerId)

              setActiveStep({ type: Step.ProofOfAddressDocuments })
            }}
          />
        )}

        {activeStep.type === Step.ProofOfAddressDocuments && (
          <ProofOfAddressDocumentsScreen
            bankEligibility={bankEligibilityQuery.data}
            isPending={bankEligibilityQuery.isPending}
            ownerId={ownerId}
            onBack={() => {
              setActiveStep({ type: Step.ProofOfAddress })
            }}
          />
        )}
      </AnimatePresence>
    </FullScreen>
  )
}
